import { Button, Chip, Select } from '@sjuni/ui';
import { ChevronDown, ChevronUp, Copy, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MediaRow } from '@/routes/media/types';

import { type BlockType, BLOCK_TYPES, type PageBlock } from '../types';

import { BlockFields } from './BlockFields';

/**
 * docs/03-DATA-MODEL.md §7.1's block array, as a screen.
 *
 * An ordered list of collapsible panels rather than a canvas: a page body here
 * is a regulation or an admissions procedure, and the thing an editor does most
 * is find the third paragraph and change a date. Everything folds shut so that
 * an eleven-block page fits on one screen.
 *
 * Blocks are addressed by index, which is safe because this component owns the
 * whole array — but it does mean the open panel is tracked by index too, and a
 * move has to carry it. See `moveBy`.
 */
interface Props {
  value: PageBlock[];
  onChange: (blocks: PageBlock[]) => void;
  /** The library images the saved blocks reference. */
  media: MediaRow[];
  /** Server-side messages, which name a block by its one-based number. */
  error?: string;
}

export function BlockEditor({ value, onChange, media, error }: Props) {
  const { t } = useTranslation();

  const [open, setOpen] = useState<number | null>(value.length === 0 ? null : 0);
  const [adding, setAdding] = useState<BlockType>('rich_text');

  /*
   * What the pickers draw from: the images the page arrived with, plus any the
   * editor has chosen since. Without the second half, picking an image would
   * leave an empty frame until the page was saved and reloaded — the id is in
   * the block by then, but nothing on the screen knows what it looks like.
   */
  const [known, setKnown] = useState<Record<number, MediaRow>>({});

  const pickers: Record<number, MediaRow> = { ...byId(media), ...known };

  function replace(index: number, block: PageBlock): void {
    onChange(value.map((current, position) => (position === index ? block : current)));
  }

  function moveBy(index: number, delta: number): void {
    const target = index + delta;

    if (target < 0 || target >= value.length) {
      return;
    }

    const next = [...value];
    [next[index], next[target]] = [next[target] as PageBlock, next[index] as PageBlock];

    onChange(next);

    // The open panel follows the block it belongs to. Without this, moving the
    // block you are editing collapses it and opens its neighbour.
    setOpen((current) => (current === index ? target : current === target ? index : current));
  }

  function remove(index: number): void {
    onChange(value.filter((_, position) => position !== index));
    setOpen(null);
  }

  function add(): void {
    onChange([...value, { type: adding, data: emptyData(adding) }]);
    setOpen(value.length);
  }

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-h4 font-extrabold text-primary">{t('pages.blocks.title')}</h2>
        <p className="text-caption text-muted">
          {t('pages.blocks.count', { count: value.length })}
        </p>
      </div>

      {error ? (
        <p role="alert" className="bg-danger-soft rounded-card p-3 text-body-sm text-danger">
          {error}
        </p>
      ) : null}

      <ol className="space-y-2">
        {value.map((block, index) => {
          const isOpen = open === index;
          const panelId = `block-panel-${index}`;

          return (
            <li key={index} className="rounded-card border border-subtle">
              <div className="flex items-center gap-2 p-3">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  // `min-w-0` so the summary truncates rather than pushing
                  // the actions onto a second line — a long embed URL is
                  // otherwise enough to break the row.
                  className="flex min-w-0 flex-1 items-center gap-2 text-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
                  onClick={() => setOpen(isOpen ? null : index)}
                >
                  {isOpen ? (
                    <ChevronUp size={16} strokeWidth={1.75} aria-hidden="true" />
                  ) : (
                    <ChevronDown size={16} strokeWidth={1.75} aria-hidden="true" />
                  )}

                  <span className="text-caption tabular-nums text-muted">{index + 1}</span>

                  <Chip tone={isKnown(block.type) ? 'navy' : 'gold'}>
                    {isKnown(block.type)
                      ? t(`pages.blocks.types.${block.type}`)
                      : t('pages.blocks.unknownType')}
                  </Chip>

                  <span className="truncate text-body-sm text-secondary">{summarise(block)}</span>
                </button>

                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={t('pages.blocks.moveUp', { number: index + 1 })}
                    disabled={index === 0}
                    onClick={() => moveBy(index, -1)}
                  >
                    <ChevronUp size={14} strokeWidth={1.75} aria-hidden="true" />
                  </Button>

                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={t('pages.blocks.moveDown', { number: index + 1 })}
                    disabled={index === value.length - 1}
                    onClick={() => moveBy(index, 1)}
                  >
                    <ChevronDown size={14} strokeWidth={1.75} aria-hidden="true" />
                  </Button>

                  {/* A table of fees repeated per faculty is the case this
                      exists for — five near-identical blocks. */}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={t('pages.blocks.duplicate', { number: index + 1 })}
                    onClick={() => {
                      const copy = structuredClone(block);

                      onChange([...value.slice(0, index + 1), copy, ...value.slice(index + 1)]);
                      setOpen(index + 1);
                    }}
                  >
                    <Copy size={14} strokeWidth={1.75} aria-hidden="true" />
                  </Button>

                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={t('pages.blocks.remove', { number: index + 1 })}
                    onClick={() => remove(index)}
                  >
                    <Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />
                  </Button>
                </div>
              </div>

              {isOpen ? (
                <div id={panelId} className="border-t border-subtle p-4">
                  <BlockFields
                    media={pickers}
                    onMediaPicked={(row) => setKnown((current) => ({ ...current, [row.id]: row }))}
                    block={block}
                    onChange={(data) => replace(index, { ...block, data })}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>

      <div className="flex flex-wrap items-end gap-2">
        <Select
          label={t('pages.blocks.addType')}
          value={adding}
          onChange={(event) => setAdding(event.target.value as BlockType)}
          options={BLOCK_TYPES.map((type) => ({
            value: type,
            label: t(`pages.blocks.types.${type}`),
          }))}
        />

        <Button
          type="button"
          variant="secondary"
          iconLeft={<Plus size={16} strokeWidth={1.75} aria-hidden="true" />}
          onClick={add}
        >
          {t('pages.blocks.add')}
        </Button>
      </div>
    </section>
  );
}

function isKnown(type: string): boolean {
  return (BLOCK_TYPES as readonly string[]).includes(type);
}

/**
 * A line of the block's own content, for the collapsed header.
 *
 * Without it every closed panel reads "Rich text", and a page of nine rich-text
 * blocks becomes nine identical rows.
 */
function summarise(block: PageBlock): string {
  const first = (value: unknown): string => {
    if (typeof value === 'string') {
      return value.replace(/<[^>]+>/g, ' ').trim();
    }

    if (typeof value === 'object' && value !== null) {
      for (const nested of Object.values(value)) {
        const found = first(nested);

        if (found !== '') {
          return found;
        }
      }
    }

    return '';
  };

  return first(block.data).slice(0, 120);
}

/** The starting shape for a new block of each type. */
function emptyData(type: BlockType): Record<string, unknown> {
  switch (type) {
    case 'rich_text':
      return { html: {} };
    case 'image':
      return { path: '', alt: {}, caption: {}, width: 'normal' };
    case 'gallery':
      return { images: [], layout: 'grid' };
    case 'document_list':
      return { category_id: null, document_ids: [] };
    case 'accordion':
      return { items: [{ title: {}, body: {} }] };
    case 'table':
      return { headers: ['', ''], rows: [['', '']] };
    case 'quote':
      return { text: {}, attribution: {} };
    case 'embed':
      return { provider: 'youtube', url: '' };
    case 'cta':
      return { label: {}, url: '', variant: 'primary' };
    case 'stats':
      return { items: [{ value: '', label: {} }] };
  }
}

/** The list the API sends, as the map the pickers look up by `media_id`. */
function byId(media: MediaRow[]): Record<number, MediaRow> {
  const result: Record<number, MediaRow> = {};

  for (const row of media) {
    result[row.id] = row;
  }

  return result;
}
